import _                                                          from 'underscore';
import React, { useEffect, useRef, useState }                     from 'react';
import { Checkbox, Form, Header, Icon, List, Menu, Segment, Tab } from 'semantic-ui-react';
import { Formik, FormikProps }                                    from 'formik';
import * as Yup                                                   from 'yup';
import { useLazyQuery, useQuery }                                 from '@apollo/react-hooks';

import { formatOption, runIfFunction }                                     from '@src/util';
import {
    Align,
    CorvalAccount,
    CorvalContextOwner,
    CorvalGanttDependency,
    CorvalProject,
    CorvalProjectTemplate,
    CorvalProjectVersion,
    CorvalStaticProjectActivity,
    CorvalStaticProjectSubObjective,
    CorvalSubObjective,
    CorvalUIColor,
    FlexAlign,
    FlexJustify,
    Position,
    SemanticUIColor,
    SemanticUISize,
    Size,
    Uuid
}                                                                          from '@src/types';
import { queriesHaveData, queriesLoading }                                 from '@src/store/_apolloUtil';
import { QueryDialogProps, QueryOpDialog }                                 from '../../../components/from-query/QueryOpDialog';
import { BasicDialogContainer }                                            from '../../../components/modals/BasicDialogContainer';
import { CollapsibleContentContainer, Flexbox, FlexItem, StyledContainer } from '../../../containers';
import { LoadingIndicator }                                                from '../../../components/util/loading/LoadingIndicator';
import { ObjectiveSelectionLayout }                                        from "@src/views/pages/projects/objectives/selection/ObjectiveSelectionLayout";
import { FinePrintMessage }                                                from "@src/views/components/util/messaging/FinePrintMessage";
import classNames                                                          from "classnames";
import { ItemWithIcon }                                                    from "@src/views/components/display/ItemWithIcon";
import { Cell, Grid }                                                      from "styled-css-grid";
import { ENTITY_ICONS }                                                    from "@src/constants";
import { ObjectiveQueries }                                                from "@src/queries/ObjectiveQueries";
import { UseIf }                                                           from "@src/views/components/display/EqualValues";
import { BigFriendlyMessage }                                              from "@src/views/components/util/messaging/BigFriendlyMessage";
import { DecisionEngineQueries }                                           from "@src/queries";
import { FieldDescription, FieldWrapper, SelectField, TextField }          from '@src/views/components/forms';
import { FieldLabel }                                                      from '@src/views/components/forms/formik/FieldLabel';
import { AccountQueries }                                                  from "@src/queries/AccountQueries";
import { useCurrentUser }                                                  from "@src/hooks";
import { BigGearLoadingMessage }                                           from "@src/views/components/util/loading/BigGearLoadingMessage";
import { ProjectVersionCategoriesDropdown }                                from "@src/views/components/forms/dropdowns/ProjectVersionCategoriesDropdown";
import { ActivityQueries }                                                 from "@src/queries/ActivityQueries";


export interface AddSourceProjectSubObjectivesDialogOnCompleteReturnData {
    emptySubObjectives: CorvalStaticProjectSubObjective[],
    addedActivities: CorvalStaticProjectActivity[],
    addedDependencies: CorvalGanttDependency[],
    activeDependenciesOnImport: boolean,
}

interface AddSourceProjectSubObjectivesDialogProps extends QueryDialogProps {
    projectId: Uuid,
    projectVersionId: string,
    categoryId?: Uuid,
    subCategoryId?: Uuid,
    excludedTaxonomyIds: Uuid[],
    mutationResultDataFields?: { activities: string, dependencies: string },
    showImportOptions?: boolean,

    onComplete?( data: AddSourceProjectSubObjectivesDialogOnCompleteReturnData ): void,
}

interface FormValues {
    [ SUB_OBJECTIVE_ID: string ]: boolean,
}

type NormalizedSubObjectiveDataItem = {
    id: Uuid,
    displayName: string,
    description: string,
    category: { id: Uuid, name: string, ordinal: number, description: string },
    subCategory: { id: Uuid, name: string, ordinal: number, description: string },
    subObjective: { id: Uuid },
    activities: Array<{ id: Uuid, name: string, description: string }>
}
type NormalizedSubObjectiveStateData = Array<[
    categoryId: string,
    subcategoryGroups: Array<[
        subCategoryId: string,
        subObjectiveGroups: NormalizedSubObjectiveDataItem[]
    ]>
]>


enum PROJECT_SELECT_ORIENTATION {
    CORVAL,
    PROJECT,
    TEMPLATE
}

enum SECTION_VIEW {
    SELECTION,
    PLACEMENT,
}

const AddSourceProjectSubObjectivesDialogDefaultProps = {
    projectId:                null,
    projectVersionId:         null,
    categoryId:               null,
    subCategoryId:            null,
    excludedTaxonomyIds:      [],
    mutationResultDataFields: null,
    showImportOptions:        true,
} satisfies Partial<AddSourceProjectSubObjectivesDialogProps>;

function AddSourceProjectSubObjectivesDialog( __props: AddSourceProjectSubObjectivesDialogProps ){

    const _props = { ...AddSourceProjectSubObjectivesDialogDefaultProps, ...__props };
    const {
              projectId,
              projectVersionId,
              categoryId:    selectedCategoryId,
              subCategoryId: selectedSubCategoryId,
              excludedTaxonomyIds,
              mutationResultDataFields,
              ...props
          } = _props;

    const { currentUser }                                                                           = useCurrentUser(),
          [ currentProjectSelectOrientation, setCurrentProjectSelectOrientation ]                   = useState<PROJECT_SELECT_ORIENTATION>( PROJECT_SELECT_ORIENTATION.CORVAL ),
          [ activeTabIndex, setActiveTabIndex ]                                                     = useState<number>( SECTION_VIEW.SELECTION ),

          // Data from state
          [ sourceProjectId, setSourceProjectId ]                                                   = useState<Uuid>( null ),
          [ sourceProjectVersionId, setSourceProjectVersionId ]                                     = useState<Uuid>( null ),
          [ searchInput, setSearchInput ]                                                           = useState<string>( '' ),

          // Data from project queries
          [ targetProjectTaxonomy, setTargetProjectTaxonomy ]                                       = useState<CorvalStaticProjectActivity[]>( [] ),
          [ availableProjects, setAvailableProjects ]                                               = useState<Pick<CorvalProject, 'id' | 'name' | 'organization'>[]>( [] ),
          [ availableProjectTemplates, setAvailableProjectTemplates ]                               = useState<Pick<CorvalProjectTemplate, 'id' | 'name' | 'isArchived' | 'sourceProjectVersionId'>[]>( [] ),

          // Hashed record of keyed project id -> project versions collection
          [ availableProjectVersions, setAvailableProjectVersions ]                                 = useState<Record<Uuid, Pick<CorvalProjectVersion, 'id' | 'name' | 'isActiveVersion'>[]>>( {} ),

          // Data from DEM queries, preprocessed
          [ availableDEMSubObjectives, setAvailableDEMSubObjectives ]                               = useState<NormalizedSubObjectiveDataItem[]>( [] ),
          [ availableDEMSubObjectivesByCategory, setAvailableDEMSubObjectivesByCategory ]           = useState<NormalizedSubObjectiveStateData>( [] ),

          // Data from selected sources
          [ availableSubObjectives, setAvailableSubObjectives ]                                     = useState<NormalizedSubObjectiveDataItem[]>( [] ),
          [ availableSubObjectivesByCategory, setAvailableSubObjectivesByCategory ]                 = useState<NormalizedSubObjectiveStateData>( [] ),
          [ filteredAvailableSubObjectivesByCategory, setFilteredAvailableSubObjectivesByCategory ] = useState<NormalizedSubObjectiveStateData>( [] ),

          // Additional state for import options and forms
          [ activeDependenciesOnImport, setActiveDependenciesOnImport ]                             = useState<boolean>( false ),
          subObjectiveIdToTaxonomyMapping                                                           = useRef<Map<Uuid, [ categoryId: Uuid, subCategoryId: Uuid ]>>( new Map( [] ) ),
          formikRef                                                                                 = useRef<FormikProps<FormValues>>( null );

    // [x] get all visible projects
    // [x] get all visible templates projects
    // [x] list all projects by type, then name, then version
    // [x] get all subobjectives for that project version
    // [x] select all subobjectives desired
    // [ ] optinally match taxonomy placement
    // [ ] for each subobjective selection, set taxonomy or default to source taxonomy
    // [ ] continue to submit

    // GET: All subobjectives from DEM
    const getAvailableSubObjectivesReq = useQuery<{ subObjectives: CorvalSubObjective[] }>( DecisionEngineQueries.queries.GET_SUBOBJECTIVES( `
        id
        displayName
        description
        category { id name ordinal description }
        subCategory { id name ordinal description }
    ` ), {
        fetchPolicy: 'network-only',
        variables:   {
            where: {
                or: [
                    { contextOwner: { eq: CorvalContextOwner.Corval } },
                    { contextOwnerProjectId: { eq: projectId } }
                ]
            }
        }
    } );

    // GET: Missing subobjectives for project version
    const getMissingSubObjectivesWithActivitiesReq = useQuery<{ missingSubObjectivesForProjectVersion: CorvalSubObjective[] }>( ObjectiveQueries.queries.GET_DECLINED_PROJECT_SUBOBJECTIVES( `
        id
        displayName
        description
        category { id name ordinal description }
        subCategory { id name ordinal description }
        activities { activity{ id name description } }`
    ), {
        variables:   { projectVersionId },
        skip:        ( !projectId || !projectVersionId ),
        fetchPolicy: 'network-only'
    } );

    // GET: All subobjectives from selected source project version (project or template)
    const [ getAvailableProjectSubObjectives, getAvailableProjectSubObjectivesReq ] = useLazyQuery<{ staticProjectSubObjectives: CorvalStaticProjectSubObjective[] }>( DecisionEngineQueries.queries.GET_PROJECT_SUBOBJECTIVES( `
        id
        name
        description
        subObjectiveId
        staticProjectActivities { 
            id 
            name 
            description
            categoryId
            categoryName
            categoryDescription
            categoryOrdinal
            subCategoryId
            subCategoryName
            subCategoryDescription
            subCategoryOrdinal
        }
    ` ), {
        fetchPolicy: 'network-only'
    } );

    const getTargetProjectTaxonomyReq = useQuery<{ staticProjectActivities: CorvalStaticProjectActivity[] }>(
        ActivityQueries.queries.GET_PROJECT_ACTIVITIES( `
            id 
            categoryId  
            subCategoryId
        ` ), {
            variables:   { projectVersionId },
            skip:        ( !projectId || !projectVersionId ),
            fetchPolicy: 'cache-and-network'
        } );

    // GET: List of projects and templates accessible to user
    const getAvailableProjectsReq = useQuery<{ accounts: CorvalAccount[] }>( AccountQueries.queries.GET_ACCOUNT( `
        id
        projects {
            project {
                id
                name
                isTemplateSource
                organization{
                    id 
                    name
                }
                projectVersions {
                    id
                    name
                    isActiveVersion
                }
            }
        }
        projectTemplates{
            id
            name
            isArchived
            sourceProjectVersionId
        }`
    ), {
        skip:        ( !currentUser || !currentUser.id ),
        variables:   { id: currentUser.id },
        fetchPolicy: 'network-only'
    } );

    const infoQueriesLoading = queriesLoading( [
        getAvailableProjectsReq,
        getAvailableSubObjectivesReq,
        getMissingSubObjectivesWithActivitiesReq,
        getTargetProjectTaxonomyReq,
    ] );

    const specificProjectQueriesLoading = queriesLoading( [
        getAvailableProjectSubObjectivesReq
    ] );

    const isLoading = infoQueriesLoading || specificProjectQueriesLoading;

    // Read in Corval Sub Objectives and convert them to normalized format
    const buildNormalizedSubObjectiveItemFromDeclinedDEMSubObjectives = ( corvalSubobjectives: CorvalSubObjective[] ) => {

        const formattedItems: NormalizedSubObjectiveDataItem[] = [];

        corvalSubobjectives
            .map( item => {
                formattedItems.push( {
                        ...item,
                        subObjective: {
                            id: item.id,
                        },
                        activities:   item[ 'activities' ] && !_.isEmpty( item[ 'activities' ] )
                                      ? item[ 'activities' ].map( activity => activity.activity )
                                      : null
                    } as NormalizedSubObjectiveDataItem
                );
            } );

        return formattedItems;
    }

    // Read in Static Project SubObjectives and convert them to normalized format
    const buildNormalizedSubObjectiveItemFromStaticProjectSubObjective = ( staticProjectSubobjective: CorvalStaticProjectSubObjective ) => {

        const activities = staticProjectSubobjective.staticProjectActivities;

        if( _.isEmpty( activities ) ) return null;

        const delegateActivity                                                               = activities[ 0 ],
              { categoryId, categoryName, categoryDescription, categoryOrdinal }             = delegateActivity,
              { subCategoryId, subCategoryName, subCategoryDescription, subCategoryOrdinal } = delegateActivity;

        return ( {
            id:           staticProjectSubobjective.id,
            displayName:  staticProjectSubobjective.name,
            description:  staticProjectSubobjective.description,
            category:     {
                id:          categoryId,
                name:        categoryName,
                description: categoryDescription,
                ordinal:     categoryOrdinal,
            },
            subCategory:  {
                id:          subCategoryId,
                name:        subCategoryName,
                description: subCategoryDescription,
                ordinal:     subCategoryOrdinal,
            },
            subObjective: {
                id: staticProjectSubobjective.subObjectiveId,
            },
            activities:   activities.map( ( { id, name, description } ) => ( { id, name, description } ) )

        } as NormalizedSubObjectiveDataItem );
    }

    // Create a readable hierarchy for normalized sub-objectives for render
    const buildNormalizedSubObjectiveHierarchy = ( normalizedSubObjectiveDataItems: NormalizedSubObjectiveDataItem[] ) => {

        return _.chain( normalizedSubObjectiveDataItems )
                .filter( item => item !== null )
                .filter( ( s: NormalizedSubObjectiveDataItem ) => ( !selectedCategoryId || s.category.id === selectedCategoryId ) )             // mainly for DEM purposes, but project taxonomy could be used.
                .filter( ( s: NormalizedSubObjectiveDataItem ) => ( !selectedSubCategoryId || s.subCategory.id === selectedSubCategoryId ) )    // mainly for DEM purposes, but project taxonomy could be used.
                .filter( ( s: NormalizedSubObjectiveDataItem ) => !( excludedTaxonomyIds.includes( s.id ) ) )                                   // mainly for DEM purposes, but project taxonomy could be used.
                .groupBy( item => item.category.id )
                .mapObject( categoryGroup =>
                    _.chain( categoryGroup )
                     .groupBy( item => item.subCategory.id )
                     .pairs()
                     .value()
                )
                .pairs()
                .sortBy( ( [ categoryId ] ) =>
                    normalizedSubObjectiveDataItems.find( item => item?.category.id === categoryId )?.category.name
                )
                .value() as NormalizedSubObjectiveStateData;
    }

    useEffect( () => {

        // Clear selected project / version
        setSourceProjectId( null )
        setSourceProjectVersionId( null )
        resetTaxonomyMapping();

        // Clear all selected items
        formikRef.current?.resetForm();

    }, [ currentProjectSelectOrientation ] );

    useEffect( () => {

        if( sourceProjectId ){

            if( currentProjectSelectOrientation === PROJECT_SELECT_ORIENTATION.PROJECT ||
                currentProjectSelectOrientation === PROJECT_SELECT_ORIENTATION.TEMPLATE ){
                setAvailableSubObjectives( [] )
                setAvailableSubObjectivesByCategory( [] )
                setFilteredAvailableSubObjectivesByCategory( [] )
                resetTaxonomyMapping()

                // Set initial version if there is only one available
                if( sourceProjectId && availableProjectVersions[ sourceProjectId ].length === 1 ){

                    const projectVersionId = availableProjectVersions[ sourceProjectId ][ 0 ].id;

                    formikRef.current?.setFieldValue( 'selectedProjectVersionId', projectVersionId );

                    setSourceProjectVersionId( projectVersionId );
                }
            }
        }

    }, [ sourceProjectId ] );

    useEffect( () => {

        if( sourceProjectVersionId ){

            if( currentProjectSelectOrientation === PROJECT_SELECT_ORIENTATION.PROJECT ||
                currentProjectSelectOrientation === PROJECT_SELECT_ORIENTATION.TEMPLATE ){

                getAvailableProjectSubObjectives( {
                    variables: {
                        projectVersionId: sourceProjectVersionId,
                    }
                } );
            }
        }

    }, [ sourceProjectVersionId ] );

    useEffect( () => {

        if( !searchInput ){

            // Reset
            setFilteredAvailableSubObjectivesByCategory( availableSubObjectivesByCategory )
            return;
        }

        // Filter by search input
        const filteredAvailableSubObjectives = availableSubObjectives.filter( subobjective => {

            const nameContainsSearch       = subobjective.displayName.toLowerCase().includes( searchInput.toLowerCase() ),
                  activitiesContainsSearch = _.some( subobjective.activities, activity => activity.name.toLowerCase().includes( searchInput.toLowerCase() ) );

            return ( nameContainsSearch || activitiesContainsSearch )

        } );

        setFilteredAvailableSubObjectivesByCategory( buildNormalizedSubObjectiveHierarchy( filteredAvailableSubObjectives ) )

    }, [ searchInput ] )

    useEffect( () => {

        if( sourceProjectVersionId &&
            !queriesLoading( [ getAvailableProjectSubObjectivesReq ] ) &&
            queriesHaveData( [ getAvailableProjectSubObjectivesReq ] ) ){

            const subObjectiveItems = getAvailableProjectSubObjectivesReq.data.staticProjectSubObjectives;
            const availableSubObjectives = subObjectiveItems.map( subObjectiveItem => buildNormalizedSubObjectiveItemFromStaticProjectSubObjective( subObjectiveItem ) );
            const availableSubObjectivesByCategory = buildNormalizedSubObjectiveHierarchy( availableSubObjectives ) as NormalizedSubObjectiveStateData;

            setAvailableSubObjectives( availableSubObjectives );
            setAvailableSubObjectivesByCategory( availableSubObjectivesByCategory );
            setFilteredAvailableSubObjectivesByCategory( availableSubObjectivesByCategory );
            resetTaxonomyMapping();
        }

    }, [ sourceProjectVersionId, getAvailableProjectSubObjectivesReq ] );

    useEffect( () => {

        if( !queriesLoading( [ getAvailableProjectsReq ] ) &&
            queriesHaveData( [ getAvailableProjectsReq ] ) ){

            const projectVersionMap: Record<Uuid, Pick<CorvalProjectVersion, 'id' | 'name' | 'isActiveVersion'>[]> = {};

            const account           = _.first( getAvailableProjectsReq.data.accounts ),
                  availableProjects = account.projects
                                             .filter( project => !project.project.isTemplateSource )
                                             .map( project => ( { id: project.project.id, name: project.project.name } ) );

            const availableProjectTemplates = account.projectTemplates.filter( p => !p.isArchived );

            // Map project ids to project version set
            account.projects.map( project => {
                projectVersionMap[ project.project.id ] = project.project.projectVersions
            } );

            setAvailableProjects( availableProjects );
            setAvailableProjectTemplates( availableProjectTemplates );
            setAvailableProjectVersions( projectVersionMap );
        }

    }, [ getAvailableProjectsReq ] );

    useEffect( () => {

        if( !queriesLoading( [ getAvailableSubObjectivesReq, getMissingSubObjectivesWithActivitiesReq ] ) &&
            queriesHaveData( [ getAvailableSubObjectivesReq, getMissingSubObjectivesWithActivitiesReq ] ) ){

            // Get subobjectives from the project, format records
            const subObjectiveItemsWithActivities = getMissingSubObjectivesWithActivitiesReq.data.missingSubObjectivesForProjectVersion,
                  emptySubObjectiveItems          = getAvailableSubObjectivesReq.data.subObjectives.filter( subObj => !( _.pluck( subObjectiveItemsWithActivities, 'id' ).includes( subObj.id ) ) ),
                  allCorvalSubObjectiveItems      = _.union( emptySubObjectiveItems, subObjectiveItemsWithActivities );

            // Format the sub objectives for taxonomy layout
            const availableDEMSubObjectives           = buildNormalizedSubObjectiveItemFromDeclinedDEMSubObjectives( allCorvalSubObjectiveItems ),
                  availableDEMSubObjectivesByCategory = buildNormalizedSubObjectiveHierarchy( availableDEMSubObjectives );

            setAvailableDEMSubObjectives( availableDEMSubObjectives );
            setAvailableDEMSubObjectivesByCategory( availableDEMSubObjectivesByCategory );

            // Initialize the view if orientation is Corval
            if( currentProjectSelectOrientation === PROJECT_SELECT_ORIENTATION.CORVAL ){
                setAvailableSubObjectives( availableDEMSubObjectives )
                setAvailableSubObjectivesByCategory( availableDEMSubObjectivesByCategory )
                setFilteredAvailableSubObjectivesByCategory( availableDEMSubObjectivesByCategory )
            }
        }

    }, [
        getAvailableSubObjectivesReq,
        getMissingSubObjectivesWithActivitiesReq
    ] );

    useEffect( () => {

        if( !queriesLoading( [ getTargetProjectTaxonomyReq ] ) ){

            // Get subobjectives from the project, format records
            const targetProjectTaxonomy = _.uniq( getTargetProjectTaxonomyReq.data.staticProjectActivities, a => `${ a.categoryId }-${ a.subCategoryId }` );

            setTargetProjectTaxonomy( targetProjectTaxonomy );
        }

    }, [ getTargetProjectTaxonomyReq ] );

    const getSelectedSubobjectives = () => {

        const subObjectiveIds = [];

        _.mapObject( formikRef.current?.values ?? {}, ( isIncluded, selectedId ) => {
            if( isIncluded ) subObjectiveIds.push( selectedId );
        } );

        return availableSubObjectives.filter( subObj => subObjectiveIds.includes( subObj.id ) );
    }

    const generateGraphArgs = () => {

        const selectedSubObjectiveTaxonomyMapping = getSelectedSubobjectives().map( selectedSubObjective => {

            // If we have a mapping, use it
            // If we fail to map, revert to original taxonomy
            let [ targetCategoryId, targetSubCategoryId ] = subObjectiveIdToTaxonomyMapping.current.get( selectedSubObjective.id ) ?? [ null, null ];

            return {
                categoryId:     targetCategoryId ?? selectedSubObjective.category.id,
                subCategoryId:  targetSubCategoryId ?? selectedSubObjective.subCategory.id,
                subObjectiveId: selectedSubObjective.subObjective.id,
            }
        } );

        return ( {
            variables: {
                input: {
                    targetProjectVersionId:    projectVersionId,
                    sourceProjectVersionId:    sourceProjectVersionId,
                    subObjectiveCategoryItems: selectedSubObjectiveTaxonomyMapping,
                    activateTiming:            false
                }
            }
        } );
    };

    const handleSearchInputChanged = ( value: string ) => {

        setSearchInput( value );

    };

    const resetTaxonomyMapping = () => {

        subObjectiveIdToTaxonomyMapping.current = new Map( [] );
    }

    const removeTaxonomyMappingForSubObjectiveId = ( subObjectiveId: Uuid ) => {

        subObjectiveIdToTaxonomyMapping.current.delete( subObjectiveId );
    }

    const mapSubObjectiveIdToTaxonomy = ( subObjectiveId: Uuid, targetSubCategoryId: Uuid ) => {

        const delegateTaxonomy = _.findWhere( targetProjectTaxonomy, { subCategoryId: targetSubCategoryId } ) ?? null;

        if( !delegateTaxonomy ){
            removeTaxonomyMappingForSubObjectiveId( subObjectiveId );
            return;
        }

        const { subCategoryId, categoryId } = delegateTaxonomy;

        subObjectiveIdToTaxonomyMapping.current.set( subObjectiveId, [ categoryId, subCategoryId ] );

        console.log( Array.from( subObjectiveIdToTaxonomyMapping.current ) );
    }

    const handleCategoryMappingChanged = ( subObjectiveId: Uuid, targetSubCategoryId: Uuid ) => {

        mapSubObjectiveIdToTaxonomy( subObjectiveId, targetSubCategoryId );

    }

    const handleOrientationChanged = ( orientation: PROJECT_SELECT_ORIENTATION ) => {

        setCurrentProjectSelectOrientation( orientation );

        if( orientation === PROJECT_SELECT_ORIENTATION.CORVAL ){
            setAvailableSubObjectives( availableDEMSubObjectives )
            setAvailableSubObjectivesByCategory( availableDEMSubObjectivesByCategory )
            setFilteredAvailableSubObjectivesByCategory( availableDEMSubObjectivesByCategory )
        } else {
            setAvailableSubObjectivesByCategory( [] )
            setFilteredAvailableSubObjectivesByCategory( [] )
            resetTaxonomyMapping()
        }
    }

    const handleOnComplete = ( data ) => {

        const selectedSubObjectives = getSelectedSubobjectives();

        const [ subObjectivesWithActivities, subObjectivesWithoutActivities ] = _.partition( selectedSubObjectives.filter( s => ( !_.isEmpty( s.activities ) ) ) ),
              subObjectiveIdsWithoutActivities                                = _.pluck( subObjectivesWithoutActivities, 'id' );

        runIfFunction( props.onComplete, {
            emptySubObjectives: subObjectiveIdsWithoutActivities,
            addedActivities:    data.addProjectVersionSubObjectives?.staticProjectActivities ?? [] as CorvalStaticProjectActivity[],
            addedDependencies:  data.addProjectVersionSubObjectives?.ganttDependencies ?? [] as CorvalGanttDependency[],
            activeDependenciesOnImport
        } );
    };

    // Set Formik initial values for all ids of objectives that will exist on form
    const initialValues: FormValues = ( !infoQueriesLoading && !specificProjectQueriesLoading && availableSubObjectives.length )
                                      ? _.chain( availableSubObjectives )
                                         .pluck( 'id' )
                                         .object( Array( availableSubObjectives.length ).fill( false ) )
                                         .value()
                                      : {};

    const projectOptions = _.sortBy( availableProjects.map( p => {
        return formatOption( p.id, p.name, { group: p.organization?.name } )
    } ) ?? [], 'value' );

    const projectVersionOptions = _.sortBy( availableProjectVersions[ sourceProjectId ]?.map( p => {
        return formatOption( p.id, p.name, { group: p.isActiveVersion ? 'Active' : 'Inactive' } )
    } ) ?? [], 'value' );

    const projectTemplateOptions = _.sortBy( availableProjectTemplates.map( p => {
        return formatOption( p.sourceProjectVersionId, p.name )
    } ) ?? [], 'value' );


    const selectedSubobjectives = getSelectedSubobjectives(),
          selectedOpenViewIds   = new Set();

    selectedSubobjectives.forEach( subObj => {
        selectedOpenViewIds.add( subObj.category.id );
        selectedOpenViewIds.add( subObj.subCategory.id );
        selectedOpenViewIds.add( subObj.id );
    } )

    return (

        <Formik<FormValues>
            innerRef={ formikRef }
            initialValues={ initialValues }
            isInitialValid={ false }
            enableReinitialize={ true }
            validateOnChange={ true }
            validationSchema={ Yup.object() }
            onSubmit={ null }>

            { formik => (

                infoQueriesLoading
                ? (
                    <StyledContainer padding={ Size.LG }>
                        <LoadingIndicator dimmer={ false } isActive={ true } label={ 'Loading...' }/>
                    </StyledContainer>
                ) : (
                    <QueryOpDialog
                        { ...props }
                        operationDisabled={ !projectId || !projectVersionId || isLoading || !formik.isValid }
                        operationIsHidden={ !projectId || !projectVersionId || ( availableSubObjectives.length === 0 ) }
                        mutation={ ObjectiveQueries.mutations.ADD_PROJECT_VERSION_SUBOBJECTIVES(
                            mutationResultDataFields?.activities ?? undefined,
                            mutationResultDataFields?.dependencies ?? undefined
                        ) }
                        graphArgs={ generateGraphArgs }
                        onComplete={ handleOnComplete }
                        queryOptions={ {
                            onCompleted: ( data ) => {
                                getAvailableProjectsReq.refetch();
                            }
                        } }
                        submitLabel={ 'Import Objectives' }
                        className={ 'no-padding' }
                        overflow={ true }
                        alignControls={ Align.CENTER }>

                        <Form>

                            <BasicDialogContainer
                                title={ 'Import Objectives to Project' }
                                contentMaxHeight={ '75dvh' }
                                contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] }
                                overflow={ true }
                                scrolling={ true }
                                contentHeight={ '75dvh' }
                                roundedTop
                                containerStyles={ {
                                    display:             'grid',
                                    gridTemplateRows:    `auto 1fr auto`,
                                    gridTemplateColumns: `1fr`,
                                    height:              '100%',
                                } }>

                                {/* Orientation selector */ }
                                <StyledContainer
                                    position={ Position.STICKY }
                                    top={ -20 }
                                    zIndex={ 100 }
                                    bgColor={ CorvalUIColor.WHITE }
                                    style={ {
                                        margin:  `0px -20px 0px`,
                                        padding: `20px`,
                                    } }>

                                    <FieldWrapper>
                                        <FieldLabel>Import From:</FieldLabel>
                                        <FieldDescription>Choose the source from where you want to import objectives and their activities.</FieldDescription>

                                        <Menu>

                                            {/* Import Orientations */ }
                                            { [
                                                PROJECT_SELECT_ORIENTATION.CORVAL,
                                                PROJECT_SELECT_ORIENTATION.PROJECT,
                                                PROJECT_SELECT_ORIENTATION.TEMPLATE,
                                            ].map( orientation => {

                                                const dataMap  = {
                                                          [ PROJECT_SELECT_ORIENTATION.CORVAL ]:   { icon: ENTITY_ICONS.SUBOBJECTIVES.ENTITY, label: 'Corval' },
                                                          [ PROJECT_SELECT_ORIENTATION.PROJECT ]:  { icon: ENTITY_ICONS.PROJECTS.ENTITY, label: 'Projects' },
                                                          [ PROJECT_SELECT_ORIENTATION.TEMPLATE ]: { icon: ENTITY_ICONS.PROJECTS.TEMPLATES.ENTITY, label: 'Templates' },
                                                      },
                                                      isActive = ( orientation === currentProjectSelectOrientation );

                                                return (
                                                    <Menu.Item key={ orientation }
                                                               name={ dataMap[ orientation ].label }
                                                               active={ isActive }
                                                               onClick={ () => handleOrientationChanged( orientation ) }>
                                                        <ItemWithIcon
                                                            iconComponent={
                                                                <Icon size={ SemanticUISize.LARGE }
                                                                      color={ isActive ? SemanticUIColor.PURPLE : SemanticUIColor.GREY }
                                                                      name={ dataMap[ orientation ].icon }
                                                                      fitted={ true }
                                                                />
                                                            }>

                                                            { dataMap[ orientation ].label }

                                                        </ItemWithIcon>
                                                    </Menu.Item>
                                                )
                                            } ) }

                                            {/* Search Menu */ }
                                            <Menu.Menu position='right' style={ { alignItems: 'center', paddingRight: 10 } }>
                                                <TextField
                                                    fluid
                                                    name={ 'searchFilter' }
                                                    icon={ 'search' }
                                                    iconPosition={ 'left' }
                                                    placeholder={ 'Search Dataset...' }
                                                    debounce={ true }
                                                    debounceValue={ searchInput }
                                                    value={ searchInput }
                                                    onDebounceChange={ handleSearchInputChanged }
                                                    action={ _.isEmpty( searchInput ) ? null : {
                                                        icon:    'delete',
                                                        basic:   true,
                                                        size:    'mini',
                                                        onClick: () => handleSearchInputChanged( '' ),
                                                    } }
                                                    style={ {
                                                        clearable: true,
                                                        minWidth:  200,
                                                    } }
                                                />
                                            </Menu.Menu>

                                        </Menu>

                                    </FieldWrapper>

                                    {/* Project / Version selectors */ }
                                    <UseIf x={ currentProjectSelectOrientation !== PROJECT_SELECT_ORIENTATION.CORVAL } eq={ true }>
                                        <Grid columns={ `1fr 1fr` } columnGap={ '10px' } rowGap={ '0px' }>

                                            <UseIf x={ currentProjectSelectOrientation } eq={ PROJECT_SELECT_ORIENTATION.TEMPLATE }>
                                                <Cell>
                                                    <SelectField
                                                        name={ 'selectedTemplateId' }
                                                        value={ sourceProjectVersionId }
                                                        placeholder={ 'Select template' }
                                                        search={ true }
                                                        options={ projectTemplateOptions }
                                                        onChange={ value => {
                                                            setSourceProjectVersionId( value )
                                                        } }
                                                    />
                                                </Cell>
                                            </UseIf>

                                            <UseIf x={ currentProjectSelectOrientation } eq={ PROJECT_SELECT_ORIENTATION.PROJECT }>
                                                <Cell>
                                                    <SelectField
                                                        name={ 'selectedProjectId' }
                                                        placeholder={ 'Select project' }
                                                        value={ sourceProjectId }
                                                        options={ projectOptions }
                                                        onChange={ value => {
                                                            setSourceProjectId( value )
                                                        } }
                                                    />
                                                </Cell>
                                                <Cell>
                                                    <SelectField
                                                        useGroupNames={ true }
                                                        disabled={ !sourceProjectId }
                                                        placeholder={ 'Select project version' }
                                                        name={ 'selectedProjectVersionId' }
                                                        value={ sourceProjectVersionId }
                                                        options={ projectVersionOptions }
                                                        onChange={ value => {
                                                            setSourceProjectVersionId( value )
                                                        } }
                                                    />
                                                </Cell>
                                            </UseIf>
                                        </Grid>
                                    </UseIf>

                                </StyledContainer>

                                <Tab activeIndex={ activeTabIndex }
                                     renderActiveOnly={ false }
                                     onTabChange={ ( event, data ) => {
                                         setActiveTabIndex( data.activeIndex as number )
                                     } }
                                     menu={ {
                                         pointing:  true,
                                         secondary: true,
                                         attached:  false,
                                         style:     {
                                             position:        'sticky',
                                             top:             currentProjectSelectOrientation === PROJECT_SELECT_ORIENTATION.CORVAL ? 120 : 175,
                                             zIndex:          50,
                                             padding:         '0 20px',
                                             margin:          `0px -20px 20px`,
                                             backgroundColor: `var(--color-white)`,
                                         }
                                     } }
                                     panes={ [
                                         {
                                             /* Subobjective selection */
                                             menuItem: 'Objective Selection',
                                             pane:     {
                                                 active:    ( activeTabIndex === SECTION_VIEW.SELECTION ),
                                                 className: 'no-border no-padding',
                                                 content:   (
                                                                <Tab.Pane className={ 'no-border no-padding' } key={ SECTION_VIEW.SELECTION }>

                                                                    {/* No Subobjectives Available */ }
                                                                    <UseIf x={ !specificProjectQueriesLoading && _.isEmpty( availableSubObjectivesByCategory ) } eq={ true }>
                                                                        <BigFriendlyMessage
                                                                            title={ 'No Objectives Available' }
                                                                            content={ (
                                                                                <>
                                                                                    <p>There are no objectives available, or you have not specified a project.</p>
                                                                                    <p>Make sure you have a template or project (and version) selected, and that the project has objectives available.</p>
                                                                                </>
                                                                            ) }
                                                                        />
                                                                    </UseIf>

                                                                    {/* No SubObjective / Activity Search Matched */ }
                                                                    <UseIf x={ !_.isEmpty( availableSubObjectivesByCategory ) && _.isEmpty( filteredAvailableSubObjectivesByCategory ) } eq={ true }>
                                                                        <BigFriendlyMessage
                                                                            title={ 'No Objectives Found' }
                                                                            content={ (
                                                                                <>
                                                                                    <p>There are no objectives that match your search criteria.</p>
                                                                                    <p>Try refining your search or creating new objectives for this project.</p>
                                                                                </>
                                                                            ) }
                                                                        />
                                                                    </UseIf>

                                                                    {/* Loading on project data retrieval */ }
                                                                    <UseIf x={ specificProjectQueriesLoading } eq={ true }>
                                                                        <StyledContainer padding={ Size.LG } position={ Position.RELATIVE } style={ { minHeight: '300px' } }>
                                                                            <BigGearLoadingMessage active={ true } label={ 'Retrieving Project Data...' }/>
                                                                        </StyledContainer>
                                                                    </UseIf>

                                                                    {/* List of objectives to select */ }
                                                                    <UseIf x={ specificProjectQueriesLoading } eq={ false }>

                                                                        { filteredAvailableSubObjectivesByCategory.map( ( [ categoryId, subcategoryGroups ] ) => {

                                                                            const categoryName = availableSubObjectives.find( o => o.category.id === categoryId )?.category.name;

                                                                            return (
                                                                                <Segment key={ categoryId } raised className={ 'no-padding' }>

                                                                                    <CategoryContainer
                                                                                        isActive={ _.isString( selectedCategoryId ) }
                                                                                        categoryId={ categoryId }
                                                                                        categoryName={ categoryName }>

                                                                                        <List relaxed={ 'very' } divided>

                                                                                            { subcategoryGroups.map( ( [ subCategoryId, availableSubObjectivesInSubCategory ] ) => {

                                                                                                const { name: subCategoryName, description: subCategoryDescription } = availableSubObjectives.find( o => o.subCategory.id === subCategoryId )?.subCategory;

                                                                                                return (
                                                                                                    <List.Item key={ subCategoryId }>
                                                                                                        <SubCategoryContainer
                                                                                                            isActive={ _.isString( selectedSubCategoryId ) }
                                                                                                            subCategoryId={ subCategoryId }
                                                                                                            subCategoryName={ subCategoryName }
                                                                                                            subCategoryDescription={ subCategoryDescription }>

                                                                                                            { availableSubObjectivesInSubCategory.map( availableSubObjective => (

                                                                                                                <Segment raised key={ availableSubObjective.id } className={ 'no-padding' }>

                                                                                                                    <StyledContainer rounded={ true }
                                                                                                                                     borderSize={ Size.MD }
                                                                                                                                     padding={ [ Size.SM, Size.MD ] }
                                                                                                                                     className={ classNames( {
                                                                                                                                         'border--hover':          !formik.values[ availableSubObjective.id ],
                                                                                                                                         'border--colored-purple': !formik.values[ availableSubObjective.id ],
                                                                                                                                         'border--colored-teal':   formik.values[ availableSubObjective.id ],
                                                                                                                                     } ) }
                                                                                                                                     onClick={ () => formik.setFieldValue( availableSubObjective.id, !formik.values[ availableSubObjective.id ] ) }>

                                                                                                                        <Grid columns={ `auto 1fr` } columnGap={ '10px' } rowGap={ '0px' }>

                                                                                                                            <Cell className={ 'padding-md-vertical' }>

                                                                                                                                <Icon name={ ENTITY_ICONS.SUBOBJECTIVES.ENTITY } color={ 'teal' }/>

                                                                                                                            </Cell>
                                                                                                                            <Cell>
                                                                                                                                <ObjectiveSelectionLayout
                                                                                                                                    onChange={ ( e, data ) => formik.setFieldValue( availableSubObjective.id, data.checked ) }
                                                                                                                                    fieldValueName={ availableSubObjective.id }
                                                                                                                                    item={ availableSubObjective as any }
                                                                                                                                    showLastUpdated={ false }
                                                                                                                                    additionalContent={
                                                                                                                                        <>
                                                                                                                                            <UseIf x={ _.isNull( availableSubObjective.activities ) } eq={ true }>

                                                                                                                                                <FinePrintMessage class={ 'text--center' }>This objective does not have any activities yet.</FinePrintMessage>

                                                                                                                                            </UseIf>
                                                                                                                                            <UseIf x={ _.isNull( availableSubObjective.activities ) } eq={ false }>

                                                                                                                                                <CollapsibleContentContainer
                                                                                                                                                    borderless={ true }
                                                                                                                                                    contentClasses={ 'no-padding' }
                                                                                                                                                    onToggle={ ( isActive, collapseDurationMS, e ) => e.stopPropagation() }
                                                                                                                                                    headerComponent={ props => (
                                                                                                                                                        <Header color={ 'purple' } onClick={ props.onClick } as={ 'h5' } className={ classNames( {
                                                                                                                                                            'cursor--pointer':   true,
                                                                                                                                                            'opacity--100':      true,
                                                                                                                                                            'opacity-hover--80': true,
                                                                                                                                                            'no-margin':         true,
                                                                                                                                                        } ) }>
                                                                                                                                                            <ItemWithIcon
                                                                                                                                                                gap={ '5px' }
                                                                                                                                                                middleAlign={ true }
                                                                                                                                                                iconComponent={
                                                                                                                                                                    <Icon fitted
                                                                                                                                                                          size={ 'small' }
                                                                                                                                                                          color={ 'black' }
                                                                                                                                                                          name={ props.isActive ? 'chevron down' : 'chevron right' }
                                                                                                                                                                    />
                                                                                                                                                                }>Supporting Activities</ItemWithIcon>
                                                                                                                                                        </Header>
                                                                                                                                                    ) }>

                                                                                                                                                    <StyledContainer padding={ [ Size.SM, null ] } className={ classNames( 'margin-sm-top border-sm-top border--colored-lt-gray border-solid' ) }>
                                                                                                                                                        <List divided={ true } relaxed={ 'very' }>

                                                                                                                                                            { availableSubObjective.activities?.map( activity => (
                                                                                                                                                                <List.Item key={ activity.id }>
                                                                                                                                                                    <List.Icon>
                                                                                                                                                                        <Icon name={ ENTITY_ICONS.ACTIVITIES.ENTITY } color={ 'teal' }/>
                                                                                                                                                                    </List.Icon>
                                                                                                                                                                    <List.Content>
                                                                                                                                                                        <Header as={ 'h4' }>{ activity.name }</Header>
                                                                                                                                                                        <FinePrintMessage>{ activity.description }</FinePrintMessage>
                                                                                                                                                                    </List.Content>
                                                                                                                                                                </List.Item>
                                                                                                                                                            ) ) }

                                                                                                                                                        </List>
                                                                                                                                                    </StyledContainer>

                                                                                                                                                </CollapsibleContentContainer>
                                                                                                                                            </UseIf>
                                                                                                                                        </>
                                                                                                                                    }
                                                                                                                                />

                                                                                                                            </Cell>
                                                                                                                        </Grid>

                                                                                                                    </StyledContainer>

                                                                                                                </Segment>
                                                                                                            ) ) }

                                                                                                        </SubCategoryContainer>

                                                                                                    </List.Item>

                                                                                                );
                                                                                            } ) }

                                                                                        </List>

                                                                                    </CategoryContainer>

                                                                                </Segment>
                                                                            );
                                                                        } ) }

                                                                    </UseIf>


                                                                </Tab.Pane>
                                                            )
                                             }
                                         },
                                         {
                                             /* Category Mapping */
                                             menuItem: 'Category Mapping',
                                             pane:     {
                                                 active:    ( activeTabIndex === SECTION_VIEW.PLACEMENT ),
                                                 className: 'no-border no-padding',
                                                 content:   (
                                                                <Tab.Pane className={ 'no-border no-padding' } key={ SECTION_VIEW.PLACEMENT }>

                                                                    {/* No SubObjective / Activity Search Matched */ }
                                                                    <UseIf x={ _.isEmpty( selectedSubobjectives ) } eq={ true }>
                                                                        <BigFriendlyMessage
                                                                            title={ 'No Objectives Selected' }
                                                                            content={ (
                                                                                <>
                                                                                    <p>Choose at least one objective to before placing them into your project's category structure.</p>
                                                                                </>
                                                                            ) }
                                                                        />
                                                                    </UseIf>

                                                                    <UseIf x={ _.isEmpty( selectedSubobjectives ) } eq={ false }>

                                                                        <Grid columns={ `1fr` } columnGap={ '0px' } rowGap={ '20px' }>

                                                                            { _.chain( selectedSubobjectives )
                                                                               .groupBy( s => ( `${ s.category.name } / ${ s.subCategory.name }` ) )
                                                                               .pairs()
                                                                               .map( ( [ groupName, selectedSubobjectives ] ) => {

                                                                                   const categoryName = groupName.split( ' / ' ).shift()
                                                                                   const subCategoryName = groupName.split( ' / ' ).pop()

                                                                                   return (
                                                                                       <Cell key={ `${ groupName }` }>

                                                                                           <StyledContainer padding={ Size.MD }>

                                                                                               {/* Source Taxonomy Header*/ }
                                                                                               <Header color={ SemanticUIColor.GREY } as={ 'h4' }>{ categoryName }</Header>
                                                                                               <Header color={ SemanticUIColor.PURPLE } as={ 'h5' } className={ 'no-margin' }>
                                                                                                   <ItemWithIcon gap={ '0px' } middleAlign={ true } iconComponent={ <Icon fixed name={ 'folder open outline' } color={ 'teal' }/> }>{ subCategoryName }</ItemWithIcon>
                                                                                               </Header>

                                                                                               <Segment raised>

                                                                                                   <Grid columns={ `1fr` } columnGap={ '0px' } rowGap={ '20px' }>

                                                                                                       { selectedSubobjectives.map( selectedSubobjective => {

                                                                                                           const {
                                                                                                                     category:    { id: categoryId },
                                                                                                                     subCategory: { id: subCategoryId },
                                                                                                                     displayName, description,
                                                                                                                 } = selectedSubobjective;

                                                                                                           return (

                                                                                                               <Cell key={ `${ selectedSubobjective.id }` } className={ 'padding-md-horizontal' }>

                                                                                                                   <Grid columns={ `1fr auto 1fr` } columnGap={ '10px' } rowGap={ '0px' }>
                                                                                                                       <Cell>
                                                                                                                           <Header as={ 'h4' }>{ displayName }</Header>
                                                                                                                           <FinePrintMessage>{ description }</FinePrintMessage>
                                                                                                                       </Cell>
                                                                                                                       <Cell middle>
                                                                                                                           <Icon name={ 'arrow right' } color={ 'teal' }/>
                                                                                                                       </Cell>
                                                                                                                       <Cell middle>
                                                                                                                           <ProjectVersionCategoriesDropdown
                                                                                                                               name={ `${ selectedSubobjective.id }-mapping-taxonomy` }
                                                                                                                               projectId={ projectId }
                                                                                                                               projectVersionId={ projectVersionId }
                                                                                                                               categoriesOnly={ false }
                                                                                                                               placeholder={ '(Source Category)' }
                                                                                                                               onChange={ ( value ) => {
                                                                                                                                   handleCategoryMappingChanged( selectedSubobjective.id, value )
                                                                                                                               } }
                                                                                                                           />
                                                                                                                       </Cell>
                                                                                                                   </Grid>

                                                                                                               </Cell>
                                                                                                           )

                                                                                                       } ) }
                                                                                                   </Grid>
                                                                                               </Segment>

                                                                                           </StyledContainer>
                                                                                       </Cell>
                                                                                   )

                                                                               } )
                                                                               .value() }
                                                                        </Grid>


                                                                    </UseIf>
                                                                </Tab.Pane>
                                                            )
                                             }
                                         },
                                     ] }>

                                </Tab>


                                {/* Import Options */ }
                                { props.showImportOptions && (

                                    <StyledContainer
                                        padding={ Size.MD }
                                        position={ Position.STICKY }
                                        bgColor={ CorvalUIColor.WHITE }
                                        bottom={ `-50px` }
                                        style={ {
                                            margin:    `0 -20px -50px -20px`,
                                            borderTop: '1px solid var(--color-lightGrey)',
                                        } }>
                                        <Flexbox align={ FlexAlign.CENTER } justify={ FlexJustify.START } gutterSize={ Size.MD }>
                                            <FlexItem noGrow>
                                                <FieldWrapper>
                                                    <FieldLabel className={ 'no-margin' }>Import Options:</FieldLabel>
                                                </FieldWrapper>
                                            </FlexItem>
                                            <FlexItem>
                                                <Checkbox fitted={ true }
                                                          id={ 'activeDependenciesOnImport' }
                                                          name={ 'activeDependenciesOnImport' }
                                                          checked={ activeDependenciesOnImport }
                                                          label={ `Activate All Dependencies` }
                                                          onClick={ ( e, data ) => {
                                                              setActiveDependenciesOnImport( data.checked );
                                                          } }
                                                />
                                            </FlexItem>
                                        </Flexbox>
                                    </StyledContainer>

                                ) }

                            </BasicDialogContainer>
                        </Form>

                    </QueryOpDialog>
                )
            ) }

        </Formik>
    )
        ;
}

const CategoryContainer = React.memo( ( props: {
    categoryId?: Uuid,
    categoryName: string,
    isActive?: boolean,
    children: any
} ) => (

    <CollapsibleContentContainer
        isActive={ props.isActive }
        borderless={ true }
        contentClasses={ 'padding-lg-left padding-md-vertical' }
        title={ props.categoryName }>

        { props.children }

    </CollapsibleContentContainer>
) );

const SubCategoryContainer = React.memo( ( props: {
    subCategoryId?: Uuid,
    subCategoryName: string,
    subCategoryDescription?: string,
    isActive?: boolean,
    children: any
} ) => (

    <CollapsibleContentContainer
        isActive={ props.isActive }
        borderless={ true }
        contentClasses={ 'padding-sm-right' }
        headerComponent={ headerProps => (
            <>
                <Header color={ 'purple' } onClick={ headerProps.onClick } as={ 'h3' } className={ classNames( {
                    'cursor--pointer':   true,
                    'opacity--100':      true,
                    'opacity-hover--80': true,
                } ) }>
                    <ItemWithIcon
                        gap={ '5px' }
                        middleAlign={ true }
                        iconComponent={
                            <Icon fitted
                                  size={ 'small' }
                                  color={ 'black' }
                                  name={ headerProps.isActive ? 'chevron down' : 'chevron right' }
                            />
                        }>{ props.subCategoryName }</ItemWithIcon>
                </Header>
                <FinePrintMessage fadeIn={ false } className={ 'padding-md-left padding-sm-top' }>{ props.subCategoryDescription }</FinePrintMessage>
            </>
        ) }>

        <Segment secondary>

            { props.children }

        </Segment>

    </CollapsibleContentContainer>
) );

export {
    AddSourceProjectSubObjectivesDialog,
    AddSourceProjectSubObjectivesDialogDefaultProps,
    type AddSourceProjectSubObjectivesDialogProps,
};
